import { validate_contact_field, CONTACT_FIELD_TYPES } from '@/utils/validations/contact_validators';

export default {
    methods: {
        has_required_value(value) {
            if (Array.isArray(value)) {
                return value.length > 0;
            }

            if (value === null || value === undefined) {
                return false;
            }

            if (typeof value === 'string') {
                return value.trim().length > 0;
            }

            return true;
        },

        get_missing_required_options(field, value) {
            const required_options = Array.isArray(field?.required_options)
                ? field.required_options.filter(Boolean)
                : [];

            if (required_options.length === 0) {
                return [];
            }

            if (field.type === 'single_select' || field.type === 'radio') {
                const selected_value = String(value || '').trim();
                return required_options.includes(selected_value) ? [] : required_options;
            }

            if (field.type === 'multi_select' || field.type === 'checkboxes') {
                const selected_values = Array.isArray(value)
                    ? value.map((item) => String(item).trim())
                    : [];
                return required_options.filter((option) => !selected_values.includes(option));
            }

            return [];
        },

        map_question_type(type) {
            const type_map = new Map([
                ['short_text', 'text'],
                ['long_text', 'textarea'],
                ['single_select', 'single_select'],
                ['radio', 'single_select'],
                ['multi_select', 'multi_select'],
                ['checkboxes', 'multi_select'],
                ['file', 'file'],
                ['number', 'number'],
                ['date', 'date'],
                ['facial_verification', 'facial_verification'],
                ['sa_id_number', 'sa_id_number'],
                ['sa_phone_number', 'phone'],
                ['telephone', 'phone'],
                ['phone', 'phone'],
                ['fax', 'fax'],
                ['email', 'email'],
                ['postal_code', 'postal_code'],
                ['passport_number', 'passport_number'],
                ['pdf_file', 'pdf_file']
            ]);

            return type_map.get(type) || 'text';
        },

        normalize_value(value) {
            if (value === null || value === undefined) return '';
            return String(value).trim().toLowerCase();
        },

        get_required_option_error_message() {
            return '';
        },

        get_we_month_number(value) {
            if (value === '' || value === null || value === undefined) return NaN;

            const month_text = String(value).trim().toLowerCase();
            const month_values = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
            const month_index = month_values.findIndex(month => month === month_text);

            if (month_index >= 0) return month_index + 1;

            return parseInt(month_text, 10);
        },

        get_choice_text_from_value(field, value) {
            if (!value) return '';

            if (Array.isArray(value)) {
                return value.map(val => {
                    const option = field.options?.find(opt =>
                        String(opt.value) === String(val) ||
                        String(opt.text).toLowerCase() === String(val).toLowerCase()
                    );
                    return option ? option.text : val;
                }).join(', ');
            } else {
                const option = field.options?.find(opt =>
                    String(opt.value) === String(value) ||
                    String(opt.text).toLowerCase() === String(value).toLowerCase()
                );
                return option ? option.text : value;
            }
        },

        process_field(field, answers, file_uploads) {
            const val = Reflect.get(this.safe_form_data, field.model);

            if (field.type === 'file' || field.type === 'pdf_file' || field.type === 'facial_verification') {
                const comment = Reflect.get(this.safe_form_data, field.model + '_file_comment');
                const existing_url = val?.original_url || val?.presigned_url || val?.file_url || val?.url || null;

                if (val?.file) {
                    file_uploads.push({ file: val.file, id: field.section_question_id });
                    answers.push({
                        section_question_id: field.section_question_id,
                        answer_text: val.file.name,
                        question_comment: null,
                    });
                } else if (existing_url) {
                    answers.push({
                        section_question_id: field.section_question_id,
                        answer_text: existing_url,
                        question_comment: null,
                    });

                    if (comment) {
                        answers.push({
                            section_question_id: field.section_question_id,
                            answer_text: null,
                            question_comment: comment,
                        });
                    }
                } else if (val?.file_name) {
                    answers.push({
                        section_question_id: field.section_question_id,
                        answer_text: val.file_name,
                        question_comment: null,
                    });

                    if (comment) {
                        answers.push({
                            section_question_id: field.section_question_id,
                            answer_text: null,
                            question_comment: comment,
                        });
                    }
                }
            } else if (field.type === 'multi_select') {
                const answer_text = this.get_choice_text_from_value(field, val);
                answers.push({
                    section_question_id: field.section_question_id,
                    answer_text: answer_text
                });
            } else if (field.type === 'single_select' || field.type === 'radio') {
                const answer_text = this.get_choice_text_from_value(field, val);
                answers.push({
                    section_question_id: field.section_question_id,
                    answer_text: answer_text
                });
            } else {
                answers.push({
                    section_question_id: field.section_question_id,
                    answer_text: (val || '').toString().trim()
                });
            }

            field.dependent_questions.forEach(dq => {
                if (dq.is_visible) {
                    const dependent_section_question_id = Number(dq.section_question_id);
                    if (!Number.isInteger(dependent_section_question_id) || dependent_section_question_id <= 0) {
                        return;
                    }

                    const dq_val = Reflect.get(this.safe_form_data, dq.model);
                    const dq_existing_url = dq_val?.original_url || dq_val?.presigned_url || dq_val?.file_url || dq_val?.url || null;

                    if (dq.type === 'file' || dq.type === 'pdf_file' || dq.type === 'facial_verification') {
                        if (dq_val?.file) {
                            file_uploads.push({ file: dq_val.file, id: dependent_section_question_id });
                            answers.push({
                                section_question_id: dependent_section_question_id,
                                answer_text: dq_val.file.name
                            });
                        } else if (dq_existing_url) {
                            answers.push({
                                section_question_id: dependent_section_question_id,
                                answer_text: dq_existing_url
                            });
                        } else if (dq_val?.file_name) {
                            answers.push({
                                section_question_id: dependent_section_question_id,
                                answer_text: dq_val.file_name
                            });
                        }
                    } else if (dq.type === 'multi_select') {
                        const answer_text = this.get_choice_text_from_value(dq, dq_val);
                        answers.push({
                            section_question_id: dependent_section_question_id,
                            answer_text: answer_text
                        });
                    } else if (dq.type === 'single_select' || dq.type === 'radio') {
                        const answer_text = this.get_choice_text_from_value(dq, dq_val);
                        answers.push({
                            section_question_id: dependent_section_question_id,
                            answer_text: answer_text
                        });
                    } else {
                        answers.push({
                            section_question_id: dependent_section_question_id,
                            answer_text: (dq_val || '').toString().trim()
                        });
                    }
                }
            });
        },

        validate_required_fields(section) {
            const errors = {};

            // Work experience sections: validate block fields + date range
            if (section.is_work_experience && this.work_experience_blocks && this.work_experience_blocks[section.id]) {
                const blocks = this.work_experience_blocks[section.id] || [];
                blocks.forEach((block) => {
                    const field_map = {};
                    (block.fields || []).forEach(field => {
                        field_map[field.key || field.label] = field;

                        const value = Reflect.get(this.safe_form_data, field.model);

                        // Required check
                        if (field.required && (!value || (Array.isArray(value) && value.length === 0))) {
                            Reflect.set(errors, field.model, 'This field is mandatory');
                            return;
                        }

                        // Month range check (1-12)
                        if (field.type === 'we_month' && value) {
                            const n = this.get_we_month_number(value);
                            if (isNaN(n) || n < 1 || n > 12) {
                                Reflect.set(errors, field.model, 'Select a valid month');
                            }
                        }

                        // Year range check
                        if (field.type === 'we_year' && value) {
                            const n = parseInt(value, 10);
                            if (isNaN(n) || n < 1900 || n > 2100) {
                                Reflect.set(errors, field.model, 'Enter a valid year (1900–2100)');
                            }
                        }
                    });

                    // Date range validation: end date must not be before start date
                    if (!block.currently_work_here) {
                        const fm_field = field_map['from_month'] || field_map['From Month'];
                        const fy_field = field_map['from_year'] || field_map['From Year'];
                        const tm_field = field_map['to_month'] || field_map['To Month'];
                        const ty_field = field_map['to_year'] || field_map['To Year'];

                        if (fm_field && fy_field && tm_field && ty_field) {
                            const fm = this.get_we_month_number(Reflect.get(this.safe_form_data, fm_field.model));
                            const fy = parseInt(Reflect.get(this.safe_form_data, fy_field.model), 10);
                            const tm = this.get_we_month_number(Reflect.get(this.safe_form_data, tm_field.model));
                            const ty = parseInt(Reflect.get(this.safe_form_data, ty_field.model), 10);

                            if (!isNaN(fm) && !isNaN(fy) && !isNaN(tm) && !isNaN(ty)) {
                                if (ty < fy || (ty === fy && tm < fm)) {
                                    Reflect.set(errors, tm_field.model, 'End date cannot be before start date');
                                    Reflect.set(errors, ty_field.model, 'End date cannot be before start date');
                                }
                            }
                        }
                    }
                });
                return errors;
            }

            section.fields.forEach(field => {
                if (field.is_dependent) return;
                const value = Reflect.get(this.safe_form_data, field.model);

                if (field.required && !this.has_required_value(value)) {
                Reflect.set(errors, field.model, `This field is mandatory`);
                }
                if (field.type === 'sa_id_number' && this.has_required_value(value) && typeof this.validate_sa_id_field === 'function') {
                    const sa_id_result = this.validate_sa_id_field(field, value);
                    if (!sa_id_result.valid) {
                        Reflect.set(errors, field.model, sa_id_result.error);
                    }
                }
                // Contact field format validation
                if (CONTACT_FIELD_TYPES.has(field.type) && value && String(value).trim().length > 0) {
                    const contact_result = validate_contact_field(field.type, value);
                    if (!contact_result.valid) {
                        Reflect.set(errors, field.model, contact_result.error);
                    }
                }
                const missing_required_options = this.get_missing_required_options(field, value);
                if (missing_required_options.length > 0) {
                const required_option_message = this.get_required_option_error_message(field, missing_required_options);
                if (required_option_message) {
                    Reflect.set(errors, field.model, required_option_message);
                }
                }
                if (field.dependent_questions && field.dependent_questions.length) {
                    field.dependent_questions.forEach(dq => {
                        const dependent_question_value = Reflect.get(this.safe_form_data, dq.model);

                    if (dq.is_visible && dq.required && !this.has_required_value(dependent_question_value)) {
                    Reflect.set(errors, dq.model, `Field required "${dq.label || 'this field'}"`);
                    }
                    if (dq.is_visible && dq.type === 'sa_id_number' && this.has_required_value(dependent_question_value) && typeof this.validate_sa_id_field === 'function') {
                        const sa_id_result = this.validate_sa_id_field(dq, dependent_question_value);
                        if (!sa_id_result.valid) {
                            Reflect.set(errors, dq.model, sa_id_result.error);
                        }
                    }
                    const missing_dependent_options = this.get_missing_required_options(dq, dependent_question_value);
                    if (dq.is_visible && missing_dependent_options.length > 0) {
                    const dependent_option_message = this.get_required_option_error_message(dq, missing_dependent_options);
                    if (dependent_option_message) {
                        Reflect.set(errors, dq.model, dependent_option_message);
                    }
                    }
                });
                }
            });
            return errors;
        },
    }
}
